import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Megaphone, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  avviso: { label: "Avviso", icon: Megaphone, cls: "bg-accent/10 text-accent border-accent/30" },
  feature: { label: "Nuova feature", icon: Sparkles, cls: "bg-primary/10 text-primary border-primary/30" },
};

const emptyAnn = { title: "", body: "", type: "avviso", link: "" };

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/announcements");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...emptyAnn }); setOpen(true); };
  const openEdit = (a) => { setEditing({ ...a }); setOpen(true); };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Inserisci un titolo"); return; }
    try {
      if (editing.id) await api.put(`/announcements/${editing.id}`, editing);
      else await api.post("/announcements", editing);
      toast.success("Comunicazione salvata");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.title}"?`)) return;
    await api.delete(`/announcements/${a.id}`);
    toast.success("Eliminata");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-3xl font-extrabold text-navy tracking-tight">Comunicazioni</h1>
        <Button onClick={openNew} data-testid="add-announcement-button" className="gap-2 font-bold"><Plus size={18} /> Nuova comunicazione</Button>
      </div>
      <p className="text-muted-foreground mb-8">Avvisi e nuove feature visibili a tutti i commerciali nella loro dashboard.</p>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="border border-dashed border-border rounded-2xl p-16 text-center bg-white">
            <Megaphone className="mx-auto text-muted-foreground mb-3" size={40} />
            <p className="text-navy font-bold">Nessuna comunicazione</p>
          </div>
        )}
        {items.map((a) => {
          const ty = TYPES[a.type] || TYPES.avviso;
          return (
            <div key={a.id} data-testid={`announcement-row-${a.id}`} className="bg-white border border-border rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${ty.cls} mb-2`}>
                    <ty.icon size={12} /> {ty.label}
                  </span>
                  <p className="font-extrabold text-navy">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{a.body}</p>
                  {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="text-sm text-accent font-semibold inline-flex items-center gap-1 mt-2 hover:underline"><ExternalLink size={13} /> Scopri di più</a>}
                  <p className="text-xs text-muted-foreground mt-2">{(a.created_at || "").slice(0, 10)} · {a.created_by}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => openEdit(a)} data-testid={`edit-announcement-${a.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Pencil size={16} /></button>
                  <button onClick={() => remove(a)} data-testid={`delete-announcement-${a.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica comunicazione" : "Nuova comunicazione"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Titolo</Label>
                <Input value={editing.title} data-testid="announcement-title-input" onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="announcement-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avviso">Avviso</SelectItem>
                    <SelectItem value="feature">Nuova feature</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Testo</Label>
                <Textarea value={editing.body} rows={4} data-testid="announcement-body-input" onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Link "Scopri di più" (opzionale)</Label>
                <Input value={editing.link} data-testid="announcement-link-input" onChange={(e) => setEditing({ ...editing, link: e.target.value })} placeholder="https://..." className="mt-1.5" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-announcement-button" className="font-bold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
